import React from "react";
import {
  ShoppingBag,
  Boxes,
  CreditCard,
  BarChart3,
  Users,
  Building2,
  WifiOff,
  Sparkles,
  ShieldCheck,
  Bell,
  Barcode,
  RefreshCw,
  WalletCards,
} from "lucide-react";
import PageHero from "@/components/landing/PageHero";
import PageBottomCTA from "@/components/landing/PageBottomCTA";
import PhoneFrame from "@/components/landing/PhoneFrame";
import { MOCKUPS } from "@/constants/mockups";
import PageMeta from "@/components/PageMeta";

const FEATURES = [
  {
    icon: ShoppingBag,
    title: "Sell-first workspace",
    desc: "Designed for shop counters. Record a sale in seconds — type the product name, scan a barcode, or tap a recent item.",
    bullets: ["Fast cart with quantity steppers", "Barcode scanner support", "Custom price overrides", "Receipt history"],
    mockup: MOCKUPS.sales,
  },
  {
    icon: Boxes,
    title: "Inventory that warns you",
    desc: "Add items in one tap. Low-stock and out-of-stock alerts mean you restock before a customer walks away empty-handed.",
    bullets: ["Low-stock & out-of-stock alerts", "Add by barcode or manually", "+1 / +10 / custom quick adjusts", "Per-branch stock levels"],
    mockup: MOCKUPS.inventory,
  },
  {
    icon: CreditCard,
    title: "Customer debt tracker",
    desc: "Every credit sale is logged with the customer and amount. See unpaid balances at a glance and record repayments without losing the history.",
    bullets: ["Mark any sale as 'sold on credit'", "Customer profiles & balances", "Per-customer payment history", "Polite reminders & notes"],
    mockup: MOCKUPS.debtTracker,
  },
  {
    icon: BarChart3,
    title: "Business analytics & insights",
    desc: "Daily, weekly and monthly reports turn your sales into clear answers — without a spreadsheet in sight.",
    bullets: ["Sales trend over 7 / 30 days", "Fast-moving & slow-moving products", "Restock recommendations", "Demand forecasting"],
    mockup: MOCKUPS.dashboard,
  },
  {
    icon: Users,
    title: "Staff roles & permissions",
    desc: "Invite your staff with their own PIN login. Decide who can sell, who can edit stock, who can see reports.",
    bullets: ["PIN-based staff login", "Role-based permissions", "Per-staff sales audit", "Quick suspend / remove"],
    mockup: MOCKUPS.staffAccess,
  },
  {
    icon: Building2,
    title: "Multi-branch control",
    desc: "Move between locations without losing the wider business view. Keep sales, stock, teams, and activity correctly scoped to each branch.",
    bullets: ["Branch switching", "Per-branch sales & stock", "Combined business dashboard", "Branch-specific staff"],
    mockup: MOCKUPS.branchRegistration,
  },
  {
    icon: WalletCards,
    title: "Expenses and operating profit",
    desc: "Record operating costs beside revenue so the dashboard can show what the business kept—not only what it sold.",
    bullets: ["Categorized expense records", "Revenue and cost context", "Gross and operating profit views", "Branch-aware reporting"],
    mockup: MOCKUPS.profitCalculator,
  },
  {
    icon: Sparkles,
    title: "Shop Assistant",
    desc: "Ask questions about sales, stock, credit, analytics and sync, then get plain-language answers from your shop data.",
    bullets: ["Daily sales answers", "Stock and credit questions", "Business-aware replies", "Built for multilingual use"],
    mockup: MOCKUPS.shopAssistant,
  },
  {
    icon: Bell,
    title: "Business tips",
    desc: "Turn shop activity into practical prompts for restocking, buying patterns and top performing products.",
    bullets: ["Restock planning", "Customer buying patterns", "Fast and slow movers", "Top product tips"],
    mockup: MOCKUPS.businessTips,
  },
];

const PILLARS = [
  { icon: WifiOff, title: "Offline-first storage", body: "SQLite-powered local database keeps every sale safe even without a signal." },
  { icon: RefreshCw, title: "Smart sync queue", body: "When you reconnect, queued sales sync automatically — with retries on failure." },
  { icon: Sparkles, title: "Multilingual assistant", body: "Ask business questions in 9 languages and get answers from your real shop data." },
  { icon: Bell, title: "Smart notifications", body: "Daily summary, weekly insight and monthly report — straight to your phone." },
  { icon: Barcode, title: "Barcode support", body: "Scan to sell or scan to restock. Works with most phone cameras." },
  { icon: ShieldCheck, title: "Secure by design", body: "PIN login, OTP verification, role-based permissions and encrypted sessions." },
];

export default function Features() {
  return (
    <>
      <PageMeta title="Connected Retail Operations" description="See how Shopbook connects selling, stock, customer credit, expenses, staff, branches, analytics, and a multilingual assistant." />
      <PageHero
        eyebrow="Features"
        title="The whole day,"
        italicTail="in one dependable record."
        intro="Each workflow updates the same branch-aware operating record—from a sale at the counter to the decision an owner makes from the dashboard."
      />

      {/* Feature blocks */}
      <section className="bg-white py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-6 lg:px-12 space-y-24">
          {FEATURES.map((f, i) => {
            const reverse = i % 2 === 1;
            return (
              <article
                key={f.title}
                className="grid md:grid-cols-12 gap-10 items-center"
                data-testid={`feature-block-${i}`}
              >
                <div className={`md:col-span-7 ${reverse ? "md:order-2" : ""}`}>
                  <div className="inline-flex items-center gap-2 rounded-full bg-[#0E6B6B]/8 px-3 py-1.5 text-xs font-bold uppercase tracking-widest text-[#0E6B6B]">
                    <f.icon className="h-3.5 w-3.5" /> {String(i + 1).padStart(2, "0")}
                  </div>
                  <h2 className="mt-4 font-display text-3xl md:text-4xl lg:text-5xl font-semibold leading-[1.08] text-[#0A2424]">
                    {f.title}
                  </h2>
                  <p className="mt-4 text-lg text-[#3a4a4a] leading-relaxed max-w-xl">
                    {f.desc}
                  </p>
                  <ul className="mt-6 grid sm:grid-cols-2 gap-3 max-w-xl">
                    {f.bullets.map((b) => (
                      <li key={b} className="flex items-start gap-2 text-[15px] text-[#1c2c2c]">
                        <span className="mt-1 h-1.5 w-1.5 rounded-full bg-[#D96C4A] flex-none" />
                        {b}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className={`md:col-span-5 flex justify-center md:justify-end ${reverse ? "md:order-1 md:justify-start" : ""}`}>
                  <div className="w-[218px] sm:w-[232px]">
                    <PhoneFrame src={f.mockup} alt={`${f.title} screen`} compact />
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </section>

      {/* Pillars grid */}
      <section className="bg-[#F7FAF9] py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-6 lg:px-12">
          <div className="max-w-3xl">
            <span className="text-xs md:text-sm tracking-[0.2em] uppercase font-bold text-[#D96C4A]">The Shopbook advantage</span>
            <h2 className="mt-4 font-display text-3xl md:text-5xl font-semibold leading-[1.05] text-[#0A2424] tracking-tight">
              The strength is not one feature. It is what stays connected.
            </h2>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {PILLARS.map((p) => (
              <div
                key={p.title}
                className="rounded-3xl bg-white p-7 border border-[#0E6B6B]/8 shadow-[0_8px_30px_rgb(0,0,0,0.03)] hover:-translate-y-1 transition-transform"
              >
                <div className="h-11 w-11 rounded-2xl bg-[#0E6B6B]/8 text-[#0E6B6B] flex items-center justify-center mb-5">
                  <p.icon className="h-5 w-5" strokeWidth={1.6} />
                </div>
                <h3 className="font-display text-xl font-semibold text-[#0A2424]">{p.title}</h3>
                <p className="mt-2 text-[15px] text-[#4B5E5E] leading-relaxed">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <PageBottomCTA
        eyebrow="See it in action"
        title="Put one real business day through Shopbook."
        body="Start on the web, continue through mobile workflows, and see how sales, stock, balances, staff, expenses, and insights stay connected—without an automatic trial charge."
      />
    </>
  );
}
